import React, { useState } from "react";
import { observer } from "mobx-react";
import {
  Button,
  Divider,
  Modal,
  Stack,
  TextInput,
  Text,
  ActionIcon,
  Group,
  Alert,
} from "@mantine/core";
import {
  IconAt,
  IconCheck,
  IconClock,
  IconCopy,
  IconInfoCircle,
  IconPencil,
} from "@tabler/icons-react";
import { CopyButton } from "@mantine/core";
import { TextWithInfo } from "./textWithInfo";
import { sendToRailsController } from "../../helpers/requests";

import { Cubby } from "../../stores/Cubby";
import { MODALS } from "../../helpers/constants";
import { useViewportSize } from "@mantine/hooks";
import { TextWithInfoAndIcon } from "../dataDisplay/textWithInfoAndIcon";
import { ARTICLES } from "../../docs/docsIndex";

const ManageLightningAddress = observer(({ cubby }: { cubby: Cubby }) => {
  const { width } = useViewportSize();
  const isMobile = width < 768; // Adjust this breakpoint as needed
  const [proposedLightningAddress, setProposedLightningAddress] = useState(
    cubby.lightning_address.split("@")[0] || ""
  );
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [editMode, setEditMode] = useState(false);

  const handleLightningAddressChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    setProposedLightningAddress(value);

    // Validate the username against the regex pattern and length constraints
    const isValid = /^[a-z0-9\-_]{2,25}$/.test(value);
    if (!isValid) {
      setError(
        "A Lightning Address must be 2-25 characters long, using lowercase letters, digits, hyphens, and underscores."
      );
    } else {
      setError("");
    }
  };

  const handleCancel = () => {
    setEditMode(false);
    setProposedLightningAddress(cubby.lightning_address.split("@")[0]);
    setError("");
    cubby.setModal(null);
  };

  const handleCheckAvailability = () => {
    setLoading(true);
    sendToRailsController(
      { proposedLightningAddress, cubbyUuid: cubby.cubbyUuid },
      "/cubby_life/claim_lightning_address"
    )
      .then((response) => {
        console.log("response from claim_username", response);
        if (response.error) {
          setError(response.error);
        } else {
          cubby.handleLightningAddressChangeSuccess(proposedLightningAddress);
          setEditMode(false);
        }
      })
      .catch(() => {
        setError("An error occurred. Please try again.");
      })
      .finally(() => {
        setLoading(false);
      });
  };
  // const { width } = useViewportSize();
  // const isMobile = width < 768; // Adjust this breakpoint as needed
  // const modalWidth = "50%"; // 90% for mobile, 70% for desktop
  const makeMessage = () => {
    if (cubby.lightning_address) {
      return "While your node is running, you can receive payments at this address.";
    }
    return "Your Lightning Address is a unique identifier that allows others to send you payments over the Lightning Network.";
  };

  const makeChangeMessage = () => {
    if (cubby.number_of_additional_lightning_address_changes_allowed > 0) {
      return (
        "You can change the lightning address for this node " +
        cubby.number_of_additional_lightning_address_changes_allowed +
        " more times."
      );
    } else {
      return "You have used all of your allowed lightning address changes for this node. If you need a new lightning address, please start a new node.";
    }
  };

  return (
    <>
      <Modal
        opened={cubby.currentModal == MODALS.MANAGE_LIGHTNING_ADDRESS}
        onClose={() => handleCancel()}
        title={
          <TextWithInfoAndIcon
            text="Manage Lightning Address"
            articleSlug={ARTICLES.RECEIVE_TO_LIGHTNING_ADDRESS.slug}
            icon={<IconAt size={24} />}
            cubby={cubby}
            textSize="lg"
          />
        }
      >
        <Stack>
          <Text size="sm">{makeMessage()}</Text>
          {cubby.lightning_address && !editMode ? (
            <Group justify="center">
              <TextInput
                value={cubby.lightning_address}
                readOnly
                onClick={(e) => e.currentTarget.select()}
                style={{ width: "65%" }}
              />
              <CopyButton value={cubby.lightning_address}>
                {({ copied, copy }) => (
                  <ActionIcon
                    size="lg"
                    variant={copied ? "filled" : "default"}
                    color={copied ? "teal" : "blue"}
                    onClick={copy}
                    aria-label="copy address"
                  >
                    {copied ? <IconCheck /> : <IconCopy />}
                  </ActionIcon>
                )}
              </CopyButton>
              {cubby.number_of_additional_lightning_address_changes_allowed >
                0 && (
                <ActionIcon
                  size="lg"
                  variant="default"
                  onClick={() => setEditMode(true)}
                  aria-label="edit address"
                >
                  <IconPencil />
                </ActionIcon>
              )}
            </Group>
          ) : (
            <>
              <TextInput
                value={proposedLightningAddress}
                onChange={handleLightningAddressChange}
                placeholder="Choose a lightning address"
                error={error}
                disabled={loading}
              />

              {!error &&
                proposedLightningAddress &&
                proposedLightningAddress !==
                  cubby.lightning_address.split("@")[0] && (
                  <Button
                    onClick={handleCheckAvailability}
                    disabled={loading}
                    loading={loading}
                  >
                    Check Availability
                  </Button>
                )}
            </>
          )}

          <Alert
            variant="light"
            color="blue"
            title="Changing your lightning address"
            icon={<IconInfoCircle />}
          >
            {makeChangeMessage()}
          </Alert>

          {cubby.lightning_address && cubby.cubbyNetwork == "testnet" && (
            <Alert
              variant="light"
              color="blue"
              title="This is a testnet node"
              icon={<IconInfoCircle />}
            >
              It might be difficult to find a wallet or service which can send
              payments to this node.
            </Alert>
          )}

          {cubby.remainingDaysOfGuaranteedUptime == 0 &&
            cubby.lightning_address && (
              <Stack>
                <Alert
                  variant="light"
                  color="blue"
                  title="This node will sleep"
                  icon={<IconInfoCircle />}
                >
                  Payments to your Lightning Address will fail if your node is
                  sleeping. We recommend that you get guaranteed uptime for this
                  node.
                </Alert>

                <Button
                  leftSection={<IconClock />}
                  size="sm"
                  variant="primary"
                  aria-label="manage uptime"
                  onClick={() => cubby.setModal(MODALS.MANAGE_UPTIME)}
                >
                  Get Uptime
                </Button>
              </Stack>
            )}
        </Stack>
      </Modal>
    </>
  );
});

export default ManageLightningAddress;
